import React, {useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {defineMessages, useIntl} from 'react-intl'
import {useLocation, useHistory, Link as RouterLink} from 'react-router-dom'
import {
    Box,
    Select,
    List,
    ListItem,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {
    BLOGS_SEARCH_PER_PAGE_OPTIONS,
    BLOGS_SEARCH_SORT_OPTIONS,
    DEFAULT_BLOGS_SEARCH_PARAMS
} from '../../constants'
import Link from '@salesforce/retail-react-app/app/components/link'
import {sanitizeBlogSearchParams} from '../../utills/url'

const messages = defineMessages({
    newest: {defaultMessage: 'SORT BY: Newest', id: 'blog_listing.pagination.sort.newest'},
    oldest: {defaultMessage: 'SORT BY: Oldest', id: 'blog_listing.pagination.sort.oldest'},
    'name-ascending': {
        defaultMessage: 'SORT BY: NAME ASCENDING',
        id: 'blog_listing.pagination.sort.name_ascending'
    },
    'name-descending': {
        defaultMessage: 'SORT BY: NAME DESCENDING',
        id: 'blog_listing.pagination.sort.name_descending'
    }
})

const ArticlesPagination = ({maxPages = 1}) => {
    const {formatMessage} = useIntl()
    const location = useLocation()
    const history = useHistory()
    const [rawParams] = useSearchParams(DEFAULT_BLOGS_SEARCH_PARAMS)
    const {sz, start, sort} = sanitizeBlogSearchParams(rawParams)

    const currentPage = Math.floor(start / sz) + 1

    const sortOptions = useMemo(
        () =>
            BLOGS_SEARCH_SORT_OPTIONS.map((optionValue) => ({
                label: formatMessage(messages[optionValue]),
                value: optionValue
            })),
        [formatMessage]
    )

    const getSearchStr = (page = 1, newSz = sz, newSort = sort) => {
        const newStart = (page - 1) * newSz
        const searchFilter = {
            start: newStart,
            sz: newSz,
            sort: newSort
        }
        // omit defaults
        const searchQuery = new URLSearchParams()
        if (searchFilter.sz !== DEFAULT_BLOGS_SEARCH_PARAMS.sz)
            searchQuery.set('sz', searchFilter.sz)
        if (searchFilter.sort !== DEFAULT_BLOGS_SEARCH_PARAMS.sort)
            searchQuery.set('sort', searchFilter.sort)
        if (searchFilter.start !== DEFAULT_BLOGS_SEARCH_PARAMS.start)
            searchQuery.set('start', searchFilter.start)
        return searchQuery.toString()
    }

    const updateFilter = (page = currentPage, newSz = sz, newSort = sort) => {
        history.push({
            pathname: location.pathname,
            search: getSearchStr(page, newSz, newSort)
        })
    }

    const pageList = useMemo(() => {
        const items = []
        if (currentPage > 1) {
            items.push({label: '<', page: currentPage - 1})
            items.push({label: currentPage - 1, page: currentPage - 1})
        }
        items.push({label: currentPage, page: currentPage, isActive: true})
        if (maxPages > currentPage) {
            items.push({label: currentPage + 1, page: currentPage + 1})
            if (currentPage === 1 && maxPages > currentPage + 1) {
                items.push({label: currentPage + 2, page: currentPage + 2})
            }
            items.push({label: '>', page: currentPage + 1})
        }
        return items
    }, [currentPage, maxPages])

    useEffect(() => {
        if (currentPage > maxPages) updateFilter(maxPages)
        if (currentPage < 1) updateFilter(1)
    }, [currentPage, maxPages])

    const handleOnSortByChange = (e) => {
        updateFilter(1, sz, e.target.value)
    }

    const handleOnPerPageChange = (e) => {
        updateFilter(1, Number(e.target.value), sort)
    }

    return (
        <Stack className="articles-pagination">
            <Select id="blog-pagination-sort" value={sort} onChange={handleOnSortByChange}>
                {sortOptions.map(({label, value}) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </Select>

            <Box className="per-page-container">
                <Select id="blog-pagination-perPage" value={sz} onChange={handleOnPerPageChange}>
                    {BLOGS_SEARCH_PER_PAGE_OPTIONS.map((optVal) => (
                        <option key={optVal} value={optVal}>
                            {optVal}{' '}
                            {formatMessage({
                                defaultMessage: 'Per Page',
                                id: 'blog_listing.pagination.perPage.per_page'
                            })}
                        </option>
                    ))}
                </Select>
            </Box>

            {maxPages > 1 && (
                <Box className="pagination-detail">
                    <List className="page-list" listStyleType="none">
                        {pageList.map(({label, page, isActive = false}) => (
                            <ListItem key={label} className={isActive ? 'active' : ''}>
                                <Link
                                    as={RouterLink}
                                    variant="ghost"
                                    to={{pathname: location.pathname, search: getSearchStr(page)}}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        if (!isActive) updateFilter(page)
                                    }}
                                >
                                    {label}
                                </Link>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Stack>
    )
}

ArticlesPagination.propTypes = {
    maxPages: PropTypes.number
}

export default ArticlesPagination
